import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import StatusBadge from "./StatusBadge";
import InlineEditField from "./InlineEditField";
import RecordTimeline from "./RecordTimeline";
import RecordComments from "./RecordComments";
import { useSidekick } from "./OneSidekick";
import QuoteWonDialog from "./QuoteWonDialog";
import { Pin, PinOff, ExternalLink, History, RotateCcw, GitBranch, MessagesSquare, Sparkles, Trophy } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  customer: "Customer", product: "Product", quote: "Quotation",
  customer_po: "Customer PO", order: "Order", serial: "Serial Number",
  vendor: "Vendor", employee: "Employee",
};

const MONEY_RX = /total|price|tax|subtotal|amount|revenue/i;

const fmtValue = (label, value, cur) => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    const formatted = value.toLocaleString("en-IN", { maximumFractionDigits: 2 });
    if (!MONEY_RX.test(label)) return formatted;
    return (!cur || cur === "INR") ? `₹ ${formatted}` : `${cur} ${formatted}`;
  }
  return String(value);
};

export const QuickPreviewDrawer = ({ target, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [held, setHeld] = useState(null);
  const [schema, setSchema] = useState({ fields: {}, version: null });
  const [historyItems, setHistoryItems] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [tlKey, setTlKey] = useState(0);
  const [wonOpen, setWonOpen] = useState(false);
  const sidekick = useSidekick();
  const navigate = useNavigate();

  const loadHistory = useCallback((t = target) => {
    if (!t) return;
    api.get(`/inline/history/${t.type}/${t.id}`)
      .then(({ data }) => setHistoryItems(data.items || [])).catch(() => {});
  }, [target]);

  useEffect(() => {
    if (!target) return;
    setHeld(target);
    setData(null); setLoading(true);
    setSchema({ fields: {}, version: null });
    setHistoryItems([]); setShowHistory(false);
    const t0 = performance.now();
    api.get(`/inline/schema/${target.type}/${target.id}`)
      .then(({ data }) => setSchema(data)).catch(() => {});
    loadHistory(target);
    api.get(`/one-command/preview/${target.type}/${target.id}`)
      .then(({ data }) => {
        setData(data);
        api.post("/one-command/perf", { metric: "preview_open", ms: performance.now() - t0 }).catch(() => {});
      })
      .catch(() => toast.error("Could not load preview"))
      .finally(() => setLoading(false));
    api.get("/one-command/pins").then(({ data }) =>
      setPinned((data.items || []).some(
        (p) => p.entity_type === target.type && p.entity_id === target.id)))
      .catch(() => {});
  }, [loadHistory, target]);

  const togglePin = async () => {
    const { data } = await api.post("/one-command/pins/toggle", {
      entity_type: target.type, entity_id: target.id,
      title: target.title, subtitle: target.subtitle, route: target.route,
    });
    setPinned(data.pinned);
    toast.success(data.pinned ? "Pinned" : "Unpinned");
  };

  const openFull = () => {
    if (target?.route) navigate(target.route);
    onClose();
  };

  return (
    <Sheet open={!!target} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-0 z-[130]" data-testid="quick-preview-drawer">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100 text-left">
          <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-400">
            {TYPE_LABEL[(target || held)?.type] || "Record"} · Quick preview
          </div>
          <SheetTitle className="text-base leading-snug" data-testid="preview-title">
            {data?.title || (target || held)?.title}
          </SheetTitle>
          {data?.status && <div className="pt-1"><StatusBadge value={data.status} /></div>}
          {target?.type === "quote" && data && String(data.status || "").toUpperCase() !== "ACCEPTED" && (
            <div className="pt-2">
              <button onClick={() => setWonOpen(true)} data-testid="quote-mark-won-btn"
                className="h-8 px-3 rounded-md bg-emerald-700 text-white text-[12px] font-medium hover:bg-emerald-800 inline-flex items-center gap-1.5">
                <Trophy className="h-3.5 w-3.5" /> Mark as Won · PO Received
              </button>
            </div>
          )}
        </SheetHeader>

        <div className="px-5 py-4 space-y-4">
          {loading && (
            <div className="space-y-2.5 py-1" data-testid="preview-skeleton">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-3 w-24 bg-slate-100 rounded animate-pulse" />
                  <div className="h-3 flex-1 bg-slate-50 rounded animate-pulse" />
                </div>
              ))}
            </div>
          )}

          {data?.extra?.image_url && (
            <img src={data.extra.image_url} alt={data.title}
              className="w-full max-h-44 object-contain rounded-md border border-slate-100 bg-white" />
          )}

          {data && (
            <div className="grid grid-cols-1 gap-2">
              {(() => {
                const shown = new Set(data.fields.map((f) => f.name));
                const cur = data.fields.find((f) => /^currency$/i.test(f.label || ""))?.value;
                const extraEditable = Object.keys(schema.fields || {})
                  .filter((n) => !shown.has(n))
                  .map((n) => ({ name: n, label: schema.fields[n].label || n, value: null }));
                return [...data.fields, ...extraEditable].map((f) => {
                  const spec = f.name ? schema.fields?.[f.name] : null;
                  return (
                    <div key={f.label} className="flex items-start gap-3 text-sm">
                      <span className="w-32 shrink-0 text-[11px] uppercase tracking-wide text-slate-400 pt-0.5">{f.label}</span>
                      {spec ? (
                        <div className="min-w-0 flex-1">
                          <InlineEditField etype={target?.type} eid={target?.id}
                            field={f.name} spec={spec} value={f.value}
                            version={schema.version}
                            onSaved={(field, version, val) => {
                              setSchema((s) => ({ ...s, version }));
                              setData((d) => ({ ...d, fields: d.fields.some((x) => x.name === field)
                                ? d.fields.map((x) => x.name === field ? { ...x, value: val } : x)
                                : [...d.fields, { name: field, label: spec.label || field, value: val }] }));
                              loadHistory();
                            }} />
                        </div>
                      ) : (
                        <span className="text-slate-800 min-w-0 break-words">{fmtValue(f.label, f.value, cur)}</span>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          )}

          {schema.protected_note && (
            <div className="text-[11px] text-slate-400 italic" data-testid="preview-protected-note">
              {schema.protected_note}
            </div>
          )}

          <CollapsibleSection icon={GitBranch} label="Timeline" testId="preview-timeline-toggle">
            {target && <RecordTimeline etype={target.type} eid={target.id} refreshKey={tlKey} />}
          </CollapsibleSection>

          <CollapsibleSection icon={MessagesSquare} label="Discussion" testId="preview-comments-toggle">
            {target && (
              <RecordComments etype={target.type} eid={target.id}
                entityTitle={data?.title || target.title} entityRoute={target.route}
                onChanged={() => setTlKey((k) => k + 1)} />
            )}
          </CollapsibleSection>

          {historyItems.length > 0 && (
            <div>
              <button onClick={() => setShowHistory((s) => !s)} data-testid="preview-history-toggle"
                className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-400 mb-1.5 inline-flex items-center gap-1 hover:text-slate-700">
                <History className="h-3 w-3" /> Changes ({historyItems.length}) {showHistory ? "▾" : "▸"}
              </button>
              {showHistory && (
                <div className="space-y-1.5" data-testid="preview-history-list">
                  {historyItems.map((h) => (
                    <div key={h.id} className="border border-slate-100 rounded px-2.5 py-1.5 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-700">{h.label || h.field}</span>
                        <span className="text-slate-400 ml-auto">{(h.changed_at || "").slice(0, 16).replace("T", " ")}</span>
                      </div>
                      <div className="text-slate-600 mt-0.5">
                        <span className="line-through text-slate-400">{String(h.before ?? "—")}</span>
                        <span className="mx-1.5">→</span>
                        <span className="font-medium">{String(h.after ?? "—")}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400 mt-0.5">
                        <span>{h.changed_by} · {h.source === "restore" ? "restored" : "inline edit"}</span>
                        {h.restorable && (
                          <button data-testid={`history-restore-${h.id}`}
                            onClick={async () => {
                              try {
                                const { data: r } = await api.post("/inline/revert", { change_id: h.id });
                                toast.success("Previous value restored");
                                setSchema((s) => ({ ...s, version: r.version }));
                                setData((d) => ({ ...d, fields: d.fields.map((x) => x.name === h.field ? { ...x, value: r.restored_value } : x) }));
                                loadHistory();
                              } catch (e) {
                                toast.error(e.response?.data?.detail?.message || "Cannot restore");
                              }
                            }}
                            className="ml-auto inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 border border-slate-200 rounded px-1.5 py-0.5">
                            <RotateCcw className="h-3 w-3" /> Restore
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {data?.extra && (data.extra.quote_count !== undefined) && (
            <div className="flex gap-2">
              <Stat label="Quotes" value={data.extra.quote_count} />
              <Stat label="POs" value={data.extra.po_count ?? 0} />
              <Stat label="Orders" value={data.extra.order_count ?? 0} />
            </div>
          )}

          {data?.extra?.line_items?.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-400 mb-1.5">
                Line items ({data.extra.line_item_count})
              </div>
              <div className="space-y-1">
                {data.extra.line_items.map((li, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-700 border border-slate-100 rounded px-2 py-1.5">
                    <span className="flex-1 truncate">{li.name || "—"}</span>
                    {li.qty != null && <span className="text-slate-500">× {li.qty}</span>}
                    {li.price != null && <span className="font-mono text-slate-500">₹ {Number(li.price).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {data?.extra?.status_history?.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-400 mb-1.5">Timeline</div>
              <div className="space-y-1">
                {data.extra.status_history.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 shrink-0" />
                    <span className="capitalize">{(h.status || h.to || "").replace(/_/g, " ")}</span>
                    <span className="text-slate-400 ml-auto">{(h.at || h.timestamp || "").slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 border-t border-slate-100 bg-white px-5 py-3 flex items-center gap-2">
          <button onClick={togglePin} data-testid="preview-pin-toggle"
            className="h-8 px-3 rounded-md border border-slate-200 text-[12px] font-medium text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5">
            {pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
            {pinned ? "Unpin" : "Pin"}
          </button>
          {sidekick && (
            <button data-testid="preview-ask-sidekick"
              onClick={() => { sidekick.openWith({ etype: target.type, eid: target.id, title: data?.title || target.title, route: target.route }); onClose(); }}
              className="h-8 px-3 rounded-md border border-amber-200 text-[12px] font-medium text-amber-700 hover:bg-amber-50 inline-flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" /> Sidekick
            </button>
          )}
          <button onClick={openFull} data-testid="preview-open-full"
            className="ml-auto h-8 px-3 rounded-md bg-slate-900 text-white text-[12px] font-medium hover:bg-slate-800 inline-flex items-center gap-1.5">
            Open full record <ExternalLink className="h-3.5 w-3.5" />
          </button>
        </div>

        {target?.type === "quote" && (
          <QuoteWonDialog open={wonOpen} onOpenChange={setWonOpen}
            quoteId={target.id} quoteTitle={data?.title || target.title}
            onWon={() => {
              api.get(`/one-command/preview/${target.type}/${target.id}`)
                .then(({ data }) => setData(data)).catch(() => {});
              setTlKey((k) => k + 1);
            }} />
        )}
      </SheetContent>
    </Sheet>
  );
};

const Stat = ({ label, value }) => (
  <div className="flex-1 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-center">
    <div className="text-lg font-semibold text-slate-900">{value}</div>
    <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
  </div>
);

const CollapsibleSection = ({ icon: Icon, label, testId, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen((o) => !o)} data-testid={testId}
        className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-400 mb-1.5 inline-flex items-center gap-1 hover:text-slate-700">
        <Icon className="h-3 w-3" /> {label} {open ? "▾" : "▸"}
      </button>
      {open && children}
    </div>
  );
};

export default QuickPreviewDrawer;
